import type { TopShareholdersProps } from '@/pages/CompanyReport/components/Ownership';
import CalendarOutline from '@simplywallst/ui-core/icons/CalendarOutline';
import {
  ShareholdersDataTable,
  LoudText,
  Owner,
  RoleIcons,
  SecondaryText,
  ChangeText,
  ReportedDateTooltip,
  TooltipTerm,
  TooltipTitle,
  NoDataText,
  LighterText,
} from './styled';
import Tooltip from '@/components/Tooltip';
import { useTracking } from 'react-tracking';
import { useTranslation } from '@/hooks/useTranslation';
import { useLocale } from '@/hooks/useLocale';

const TopShareholdersTable = ({
  topShareholders,
}: Pick<TopShareholdersProps, 'topShareholders'>) => {
  const tracking = useTracking();
  const t = useTranslation();
  const locale = useLocale();

  return (
    <ShareholdersDataTable
      columnWidths="0.3,1,0.3,0.4,0.3,0.3,0.1"
      columnTextAlignments="r,l,r,r,r,l,l"
    >
      <thead>
        <tr>
          <th>
            {t('page.companyReport.ownership.topShareholder.cellHeading1')}
          </th>
          <th>
            {t('page.companyReport.ownership.topShareholder.cellHeading2')}
          </th>
          <th>
            {t('page.companyReport.ownership.topShareholder.cellHeading3')}
          </th>
          <th>
            {t('page.companyReport.ownership.topShareholder.cellHeading4')}
          </th>
          <th>
            <Tooltip
              title={
                <TooltipTitle>
                  {t(
                    'page.companyReport.ownership.topShareholder.cellHeading5Tooltip'
                  )}
                </TooltipTitle>
              }
              onOpen={() => {
                tracking.trackEvent({
                  modifier: 'jargon',
                  action: 'hover',
                  properties: { term: 'Change %' },
                });
              }}
            >
              <TooltipTerm>
                {t('page.companyReport.ownership.topShareholder.cellHeading5')}
              </TooltipTerm>
            </Tooltip>
          </th>
          <th>
            <Tooltip
              title={
                <TooltipTitle>
                  {t(
                    'page.companyReport.ownership.topShareholder.cellHeading6Tooltip'
                  )}
                </TooltipTitle>
              }
              onOpen={() => {
                tracking.trackEvent({
                  modifier: 'jargon',
                  action: 'hover',
                  properties: { term: 'Portfolio %' },
                });
              }}
            >
              <TooltipTerm>
                {t('page.companyReport.ownership.topShareholder.cellHeading6')}
              </TooltipTerm>
            </Tooltip>
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {topShareholders.map((shareholder) => (
          <tr key={shareholder.rankSharesHeld}>
            <td>
              <Owner>
                {(shareholder.percentOfSharesOutstanding * 0.01).toLocaleString(
                  locale,
                  {
                    style: 'percent',
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 2,
                    maximumSignificantDigits: 3,
                  }
                )}
              </Owner>
            </td>
            <td>
              <Owner>
                {shareholder.owner.name}
                <RoleIcons
                  boardTitle={shareholder.owner.boardTitle}
                  teamTitle={shareholder.owner.teamTitle}
                />
              </Owner>
            </td>
            <td>
              <SecondaryText>{shareholder.sharesHeldFormat}</SecondaryText>
            </td>
            <td>
              <SecondaryText>{shareholder.holdingValue}</SecondaryText>
            </td>
            <td>
              <ChangeText
                percentSharesChanged={shareholder.percentSharesChanged}
              >
                {(shareholder.percentSharesChanged * 0.01).toLocaleString(
                  locale,
                  {
                    style: 'percent',
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 2,
                    maximumSignificantDigits: 3,
                  }
                )}
              </ChangeText>
            </td>
            <td>
              {shareholder.percentOfPortfolio === 0 ? (
                <NoDataText>
                  {t('page.companyReport.ownership.topShareholder.noData')}
                </NoDataText>
              ) : (
                <LighterText>
                  {(shareholder.percentOfPortfolio * 0.01).toLocaleString(
                    locale,
                    {
                      style: 'percent',
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 2,
                    }
                  )}
                </LighterText>
              )}
            </td>
            <td>
              <SecondaryText>
                <Tooltip
                  title={
                    <ReportedDateTooltip>
                      {t(
                        'page.companyReport.ownership.topShareholder.reportedTooltip'
                      )}
                      <br />
                      <LoudText>{shareholder.holdingDate}</LoudText>
                    </ReportedDateTooltip>
                  }
                  onOpen={() => {
                    tracking.trackEvent({
                      modifier: 'date',
                      action: 'hover',
                    });
                  }}
                >
                  <CalendarOutline />
                </Tooltip>
              </SecondaryText>
            </td>
          </tr>
        ))}
      </tbody>
    </ShareholdersDataTable>
  );
};
export default TopShareholdersTable;
